/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import {
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  Flex,
  Group,
  Loader,
  LoadingOverlay,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { i18n } from '@lingui/core';
import { DatePicker, TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { closeAllModals, openConfirmModal, openModal } from '@mantine/modals';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { IconCalendarEvent, IconCheck, IconFileDescription, IconTag, IconX } from '@tabler/icons';
import ResourcePlanningS from '../../Service/restapi/resourcePlanningS';
import { Planning, PlanningGroup, ResourceType, Shortage } from '../../Types/ResourceT';
import { DateUtils } from '../../Utils/dateUtils';
import { EventDate } from '../../Types/Event';
import { DataS } from '../../Service/DataS';
import DocumentTemplateSelect from '../../Atoms/inputs/DocumentTemplateSelect';
import { DocumentS } from '../Documents/DocumentS';
import { RoutingS } from '../../Service/RoutingS';
import BookitupBadge from '../../Atoms/BookitupBadge';
import { BrowserS } from '../../Service/BrowserS';

interface PlanningGroupProps {
  interceptViolation?: (httpResponse: Response) => void;
  initial?: PlanningGroup;
  order?: number;
  eventStart?: Date;
  eventEnd?: Date;
  eventId?: string;
  onFinish: (group: PlanningGroup) => void;
  dates: EventDate[];
}

export const openPlanningGroupModal = (props: PlanningGroupProps) => {
  openModal({
    title: (
      <Text weight="bolder" size="xl">
        {props.initial ? 'Planungsgruppe bearbeiten' : 'Planungsgruppe hinzufügen'}
      </Text>
    ),
    children: <CreatePlanningModal {...props} />,
  });
};

const CreatePlanningModal: FC<PlanningGroupProps> = ({
  initial,
  order,
  eventStart,
  eventEnd,
  eventId,
  onFinish,
  interceptViolation,
  dates,
}) => {
  const EDIT_MODE = initial !== undefined;

  const form = useForm({
    initialValues: {
      description: EDIT_MODE ? initial.description : `Planungsgruppe ${order}`,
      begin: EDIT_MODE ? new Date(initial.begin) : eventStart,
      beginTime: EDIT_MODE ? new Date(initial.begin) : eventStart,
      end: EDIT_MODE ? new Date(initial.end) : eventEnd,
      endTime: EDIT_MODE ? new Date(initial.end) : eventEnd,
    },
    validate: {
      description: (value) => (value.length > 0 ? null : 'Bitte geben Sie den Namen ein'),
    },
  });
  const [loading, setLoading] = useState(false);

  const onSubmit = async () => {
    setLoading(true);
    const { description, begin, beginTime, end, endTime } = form.values;
    let newGroup;

    const from = DateUtils.mergeInterval([begin, beginTime]);
    const to = DateUtils.mergeInterval([end, endTime]);

    if (EDIT_MODE) {
      newGroup = await ResourcePlanningS.updatePlanningGroup(initial.id, {
        description,
        begin: from,
        end: to,
        usageBegin: from,
        usageEnd: to,
      });
    } else if (begin && end && eventId) {
      newGroup = await ResourcePlanningS.createPlanningGroup(
        {
          description,
          begin: from,
          end: to,
          usageBegin: from,
          usageEnd: to,
          entityId: eventId,
          entityType: 'events',
        },
        interceptViolation,
      );
    }
    setLoading(false);
    if (newGroup) {
      onFinish(newGroup);
      closeAllModals();
    }
  };

  const onEventDateSelect = (eventDateId: number) => {
    const selectedEventDate = dates.find((d) => d.id === eventDateId);
    if (selectedEventDate) {
      const { start, end } = selectedEventDate;
      const from = new Date(start);
      const to = new Date(end);
      form.setValues({ begin: from, beginTime: from, end: to, endTime: to });
    }
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Text sx={{ marginBottom: 10 }}>
        Mit Plannungsgruppen kannst du Ressourcen zu unterschiedlichen Zeiträumen einplanen.
      </Text>
      <TextInput label="Name" {...form.getInputProps('description')} />
      {dates.length > 0 && (
        <Select
          icon={<IconCalendarEvent size={20} />}
          mt="xs"
          mb="md"
          label="Veranstaltungsdatum"
          data={DataS.getEventDatesOptions(dates)}
          defaultValue={dates[0].id.toString()}
          onChange={(val) => val && onEventDateSelect(Number(val))}
        />
      )}
      <Group position="apart" mt="xs">
        <Text>Beginn:</Text>
        <Group spacing="xs">
          <DatePicker {...form.getInputProps('begin')} sx={{ width: 120 }} />
          <TimeInput {...form.getInputProps('beginTime')} />
        </Group>
      </Group>
      <Group position="apart" mt="xs">
        <Text>Ende:</Text>
        <Group spacing="xs">
          <DatePicker {...form.getInputProps('end')} sx={{ width: 120 }} />
          <TimeInput {...form.getInputProps('endTime')} />
        </Group>
      </Group>
      <Center mt={40}>
        <Button sx={{ width: 200 }} disabled={loading} type="submit" leftIcon={loading && <Loader size="xs" />}>
          {i18n._('actions.save')}
        </Button>
      </Center>
    </form>
  );
};

export const openDeleteResourcePlanning = (name: string, onConfirm: () => void) => {
  openConfirmModal({
    title: (
      <Text weight="bolder" size="xl">
        {i18n._('event.resource.planning.delete.confirm.head')}
      </Text>
    ),
    labels: { confirm: i18n._('actions.delete'), cancel: i18n._('actions.cancel') },
    children: (
      <Text>
        Möchtest Du die Ressource{' '}
        <Text span weight="bolder">
          {name}
        </Text>{' '}
        wirklich entfernen?
      </Text>
    ),
    onConfirm,
    confirmProps: { color: 'red' },
    groupProps: { spacing: 'xs' },
  });
};

interface DeleteGroupProps {
  group: PlanningGroup;
  onDelete: () => void;
}

interface CreateDeliverySlipProps {
  planningGroupId: string;
  customerId?: number;
  eventId?: number;
}

type PlanningDocumentType = 'DELIVERYNOTE' | 'PACKING_LIST';

const planningDocumentTypeOptions: { label: string; value: PlanningDocumentType }[] = [
  { label: 'Lieferschein', value: 'DELIVERYNOTE' },
  { label: 'Packliste', value: 'PACKING_LIST' },
];

type PlanningAvailability = {
  loading: boolean;
  shortage: boolean;
  shortageAmount: number;
};

type FlattenedPlanning = {
  level: number;
  planning: Planning;
};

const flattenPackablePlannings = (plannings: Planning[], level = 0): FlattenedPlanning[] =>
  plannings.flatMap((planning) => {
    const flattenedPlanning = [{ planning, level }];

    if (planning.resource.type === ResourceType.CASE) {
      return flattenedPlanning;
    }

    return [...flattenedPlanning, ...flattenPackablePlannings(planning.children ?? [], level + 1)];
  });

const isAutoSelectedSetPlanning = (planning: Planning) =>
  planning.resource.type === ResourceType.KIT && (planning.children?.length ?? 0) > 0;

const collectEffectiveSelectedPlanningIds = (plannings: Planning[], explicitSelectionIds: string[]) => {
  const effectiveSelectionIds: string[] = [];

  const visitPlanning = (planning: Planning): boolean => {
    const visibleChildren = planning.resource.type === ResourceType.CASE ? [] : planning.children ?? [];
    const childSelectedStates = visibleChildren.map(visitPlanning);
    const isSelected = isAutoSelectedSetPlanning(planning)
      ? childSelectedStates.length > 0 && childSelectedStates.every(Boolean)
      : explicitSelectionIds.includes(planning.id);

    if (isSelected && !effectiveSelectionIds.includes(planning.id)) {
      effectiveSelectionIds.push(planning.id);
    }

    return isSelected;
  };

  plannings.forEach(visitPlanning);

  return effectiveSelectionIds;
};

const getShortageState = (planning: Planning, shortages: Shortage[]) => {
  if (planning.warehousePlanning) {
    const hasWarehouseShortage = shortages.some(
      (currentShortage) =>
        (currentShortage.warehouseShortage < 0 && currentShortage.resourceType !== ResourceType.KIT) ||
        currentShortage.showWarehousePlanningShortage,
    );

    return {
      shortage: hasWarehouseShortage,
      shortageAmount: shortages.reduce(
        (largestShortage, currentShortage) =>
          Math.max(largestShortage, Math.abs(Math.min(0, currentShortage.warehouseShortage))),
        0,
      ),
    };
  }

  const hasPlanningShortage = shortages.some(
    (currentShortage) =>
      (currentShortage.shortage < 0 && currentShortage.resourceType !== ResourceType.KIT) ||
      currentShortage.showResourcePlanningShortage,
  );

  return {
    shortage: hasPlanningShortage,
    shortageAmount: shortages.reduce(
      (largestShortage, currentShortage) => Math.max(largestShortage, Math.abs(Math.min(0, currentShortage.shortage))),
      0,
    ),
  };
};

const CreateDeliverySlipModal: FC<CreateDeliverySlipProps> = ({ planningGroupId, customerId, eventId }) => {
  const [loading, setLoading] = useState(false);
  const form = useForm<{ documentType: PlanningDocumentType; template: string }>({
    initialValues: {
      documentType: 'DELIVERYNOTE',
      template: '',
    },
  });

  const onSubmit = () => {
    setLoading(true);
    DocumentS.createPlanningDocument({
      planningGroup: planningGroupId,
      documentType: form.values.documentType,
      customerId,
      eventId,
      template: form.values.template,
    })
      .then((document) => {
        if (document) {
          const targetEventId = document.eventId ?? eventId;
          const targetPath = targetEventId
            ? `/events/${targetEventId}/documents/${document.id}`
            : `/documents/${document.id}`;
          closeAllModals();
          RoutingS.openInNewTab(targetPath);
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack mt="xl" sx={{ position: 'relative' }}>
        <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ size: 'xl' }} overlayOpacity={0.3} />
        <Select
          label="Dokumenttyp"
          data={planningDocumentTypeOptions}
          icon={<IconFileDescription size={16} />}
          value={form.values.documentType}
          onChange={(value) => {
            if (value) {
              form.setValues({ documentType: value as PlanningDocumentType, template: '' });
            }
          }}
        />
        <DocumentTemplateSelect
          key={form.values.documentType}
          documentType={form.values.documentType}
          icon={<IconFileDescription size={16} />}
          {...form.getInputProps('template')}
        />
        <Center>
          <Button mt="xl" type="submit" disabled={loading} sx={{ width: 300 }}>
            {i18n._('actions.save')}
          </Button>
        </Center>
        <Text color="dimmed" align="center" sx={{ cursor: 'pointer' }} onClick={() => closeAllModals()}>
          {i18n._('actions.cancel')}
        </Text>
      </Stack>
    </form>
  );
};

export const openCreateDeliverySlipModal = (props: CreateDeliverySlipProps) =>
  openModal({
    size: 'sm',
    title: (
      <Text weight="bolder" size="xl" sx={{ lineHeight: 1.2 }}>
        Planungsdokument erstellen
      </Text>
    ),
    children: <CreateDeliverySlipModal {...props} />,
  });

const PackingPlanningRow: FC<{
  flattenedPlanning: FlattenedPlanning;
  selected: boolean;
  availability?: PlanningAvailability;
  onToggle: (planningId: string, checked: boolean) => void;
}> = ({ flattenedPlanning, selected, availability, onToggle }) => {
  const { planning, level } = flattenedPlanning;
  const { resource, plannedQuantity, exemplar } = planning;
  const disableManualSelection = isAutoSelectedSetPlanning(planning);
  const categoryName = resource.category?.name;
  const resourceDescription = BrowserS.getTextContent(resource.description)?.trim();
  const typeLabel = i18n._(`resource.type.${resource.type}`);
  let availabilityText = 'Verfügbar';
  let availabilityColor: 'dimmed' | 'red' | 'green' = 'green';

  if (availability?.loading) {
    availabilityText = 'Prüfe Verfügbarkeit...';
    availabilityColor = 'dimmed';
  } else if (availability?.shortage) {
    availabilityText = 'Nicht verfügbar';
    availabilityColor = 'red';
  }

  const serialNumber = exemplar?.serialNumber;
  const handleToggle = () => {
    if (!disableManualSelection) {
      onToggle(planning.id, !selected);
    }
  };

  return (
    <Stack spacing={0} sx={{ marginLeft: level * 18 }}>
      <Box
        py={6}
        px="sm"
        className="hooverable"
        onClick={handleToggle}
        sx={(theme) => {
          let backgroundColor = 'transparent';

          if (selected && availability?.shortage) {
            backgroundColor = theme.colorScheme === 'dark' ? theme.colors.red[9] : '#FFF5F5';
          } else if (selected) {
            backgroundColor = theme.colorScheme === 'dark' ? theme.colors.blue[9] : '#F8F9FA';
          }

          return {
            cursor: disableManualSelection ? 'default' : 'pointer',
            borderRadius: theme.radius.sm,
            backgroundColor,
          };
        }}
      >
        <Flex gap="md" align="center" wrap="nowrap">
          <Box onClick={(event: any) => event.stopPropagation()}>
            <Checkbox
              radius="xl"
              disabled={disableManualSelection}
              checked={selected}
              onChange={(event) => onToggle(planning.id, event.currentTarget.checked)}
            />
          </Box>
          <Stack spacing={2} sx={{ flexGrow: 1, minWidth: 0 }}>
            <Group position="apart" align="flex-start" noWrap>
              <Stack spacing={0} sx={{ minWidth: 0 }}>
                <Title order={6} className="overflow-ellipsis">
                  {resource.name}
                </Title>
                <Group spacing={6} my={5}>
                  {categoryName && (
                    <BookitupBadge
                      size="xs"
                      title={categoryName}
                      leftSection={<IconTag size={12} style={{ marginBottom: 2 }} />}
                    />
                  )}
                  <BookitupBadge size="xs" title={typeLabel} />
                </Group>
                {serialNumber && (
                  <Text size="xs" color="dimmed">
                    SN {serialNumber}
                  </Text>
                )}
                {resourceDescription && (
                  <Text size="xs" color="dimmed" lineClamp={1}>
                    {resourceDescription}
                  </Text>
                )}
              </Stack>
              <Stack spacing={0} align="flex-end" sx={{ minWidth: 138 }}>
                <Text size="xs" color="dimmed">
                  Menge:{' '}
                  <Text span weight={600} color="dark">
                    {plannedQuantity}
                  </Text>
                </Text>
                <Group spacing={6} noWrap>
                  {selected && (
                    <ThemeIcon radius="xl" size="xs" color={availability?.shortage ? 'red' : 'green'}>
                      {availability?.shortage ? <IconX size={12} /> : <IconCheck size={12} />}
                    </ThemeIcon>
                  )}
                  <Text size="xs" weight={600} color={availabilityColor}>
                    {availabilityText}
                  </Text>
                </Group>
                {availability?.shortage && !availability.loading && (
                  <Text size="xs" color="dimmed">
                    Mangel: {availability.shortageAmount}
                  </Text>
                )}
              </Stack>
            </Group>
          </Stack>
        </Flex>
      </Box>
      <Divider my={6} />
    </Stack>
  );
};

const CreatePackingListModal: FC<CreateDeliverySlipProps> = ({ planningGroupId, customerId, eventId }) => {
  const [loading, setLoading] = useState(false);
  const [loadingPlannings, setLoadingPlannings] = useState(true);
  const [plannings, setPlannings] = useState<Planning[]>([]);
  const [explicitSelectedPlanningIds, setExplicitSelectedPlanningIds] = useState<string[]>([]);
  const [availabilityByPlanningId, setAvailabilityByPlanningId] = useState<Record<string, PlanningAvailability>>({});
  const flattenedPlannings = useMemo(() => flattenPackablePlannings(plannings), [plannings]);
  const selectedPlanningIds = useMemo(
    () => collectEffectiveSelectedPlanningIds(plannings, explicitSelectedPlanningIds),
    [explicitSelectedPlanningIds, plannings],
  );

  const form = useForm({
    initialValues: {
      template: '',
    },
  });

  useEffect(() => {
    setLoadingPlannings(true);
    ResourcePlanningS.fetchPlannings(planningGroupId)
      .then((loadedPlannings) => {
        setPlannings(loadedPlannings);
        setExplicitSelectedPlanningIds([]);
      })
      .finally(() => setLoadingPlannings(false));
  }, [planningGroupId]);

  useEffect(() => {
    let active = true;
    if (flattenedPlannings.length === 0) {
      setAvailabilityByPlanningId({});
      return undefined;
    }

    setAvailabilityByPlanningId(
      Object.fromEntries(
        flattenedPlannings.map(({ planning }) => [planning.id, { loading: true, shortage: false, shortageAmount: 0 }]),
      ),
    );

    Promise.all(
      flattenedPlannings.map(async ({ planning }) => {
        const { planningGroup, resource, id: planningId } = planning;
        const shortages = await ResourcePlanningS.getShortages(
          resource.id,
          planningGroup?.begin,
          planningGroup?.end,
          planningGroup?.entityType,
          planningGroup?.entityId,
        );

        return [planningId, { loading: false, ...getShortageState(planning, shortages) }] as const;
      }),
    ).then((entries) => {
      if (active) {
        setAvailabilityByPlanningId(Object.fromEntries(entries));
      }
    });

    return () => {
      active = false;
    };
  }, [flattenedPlannings]);

  const togglePlanning = (planningId: string, checked: boolean) =>
    setExplicitSelectedPlanningIds((current) => {
      if (checked) {
        return current.includes(planningId) ? current : [...current, planningId];
      }

      return current.filter((id) => id !== planningId);
    });

  const onSubmit = () => {
    if (selectedPlanningIds.length === 0) {
      return;
    }

    setLoading(true);
    DocumentS.createPackingList({
      planningGroups: [planningGroupId],
      planningIds: selectedPlanningIds,
      customerId,
      eventId,
      template: form.values.template,
    })
      .then((document) => {
        if (document) {
          const targetEventId = document.eventId ?? eventId;
          const targetPath = targetEventId
            ? `/events/${targetEventId}/documents/${document.id}`
            : `/documents/${document.id}`;
          closeAllModals();
          RoutingS.openInNewTab(targetPath);
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack mt="xl" sx={{ position: 'relative' }}>
        <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ size: 'xl' }} overlayOpacity={0.3} />
        <DocumentTemplateSelect
          documentType="PACKING_LIST"
          icon={<IconFileDescription size={16} />}
          {...form.getInputProps('template')}
        />
        <Stack spacing="xs" mt="md">
          <Text size="md" weight={700}>
            Planungen
          </Text>
          {loadingPlannings && (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          )}
          {!loadingPlannings && flattenedPlannings.length === 0 && (
            <Text size="sm" color="dimmed">
              Keine Planungen vorhanden.
            </Text>
          )}
          {!loadingPlannings &&
            flattenedPlannings.map((flattenedPlanning) => (
              <PackingPlanningRow
                key={flattenedPlanning.planning.id}
                flattenedPlanning={flattenedPlanning}
                selected={selectedPlanningIds.includes(flattenedPlanning.planning.id)}
                availability={availabilityByPlanningId[flattenedPlanning.planning.id]}
                onToggle={togglePlanning}
              />
            ))}
        </Stack>
        <Center>
          <Button mt="xl" type="submit" disabled={loading || selectedPlanningIds.length === 0} sx={{ width: 300 }}>
            Packen abschließen
          </Button>
        </Center>
        <Text color="dimmed" align="center" sx={{ cursor: 'pointer' }} onClick={() => closeAllModals()}>
          {i18n._('actions.cancel')}
        </Text>
      </Stack>
    </form>
  );
};

export const openCreatePackingListModal = (props: CreateDeliverySlipProps) =>
  openModal({
    size: 'lg',
    title: (
      <Text weight="bolder" size="xl" sx={{ lineHeight: 1.2 }}>
        Packprozess
      </Text>
    ),
    children: <CreatePackingListModal {...props} />,
  });

export const openDeletePlanningGroupModal = (props: DeleteGroupProps) => {
  openConfirmModal({
    title: (
      <Text weight="bolder" size="xl">
        Planungsgruppe löschen
      </Text>
    ),
    labels: { confirm: i18n._('actions.delete'), cancel: i18n._('actions.cancel') },
    children: (
      <Text>
        Möchten Sie die Planungsgruppe{' '}
        <Text span weight="bolder">
          {props.group.description}
        </Text>{' '}
        wirklich entfernen? Alle Gruppenplanungen werden ebenfalls gelöscht.
      </Text>
    ),
    onConfirm: props.onDelete,
    confirmProps: { color: 'red' },
    groupProps: { spacing: 'xs' },
  });
};
